import { ReactElement, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import { Button, IconButton, Stack, styled } from '@mui/material'
import { Delete } from '@mui/icons-material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { yupResolver } from '@hookform/resolvers/yup'
import { Input, Modal, ModalColumnHeading, Select } from 'components'
import { getRoles, useCompaniesService } from 'services'
import { CreateUpdateUserValidationSchema } from 'utils'
import { User } from 'types'
import { successToast } from 'utils/toastService'
import { PlusIcon } from 'assets/svgComponents/PlusIcon'
import useUserService from 'services/userService'
import { RootState, useRefreshUser } from '../../../store'
import { CrudAction, Entity, usePermissions } from '../../../utils/hooks'
import { FormEditableContext } from '../../../providers'
import * as sxStyles from './dialogs.style'
import { useDeleteModal } from '../../../components/dialogs'

const IconContainer = styled(IconButton)`
  margin-left: auto;
  padding: 5px;
`

export const Form = styled('form')`
  display: flex;
  gap: 40px;
  height: 100%;
  padding: 24px 20px;
`

export const RolesWrapper = styled(Stack)`
  flex-direction: column;
  gap: 32px;
  position: relative;
  overflow: auto;
  overflow-x: hidden;
`

export const RolesContainer = styled(Stack)`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 8px 0 8px 16px;
  border-left: 2px solid ${({ theme }) => theme.palette.primary.light};
`

export const RolesInputs = styled(Stack)`
  gap: 16px;
  flex: 1;
`

export const DeleteIconContainer = styled(Stack)`
  width: 35px;
`

interface IUseUserInfoModalProps {}

const useUserInfoModal = ({}: IUseUserInfoModalProps): [
  ReactElement,
  (user?: User) => void,
] => {
  const refreshUser = useRefreshUser()
  const { checkPermission } = usePermissions()
  const currentUser = useSelector((state: RootState) => state.user.profile)
  const { data: rolesData } = useQuery('fetch-roles', getRoles)

  const queryClient = useQueryClient()
  const { createUser, updateUser, forgotPassword } = useUserService()
  const [user, setUser] = useState<User>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [formKey, setFormKey] = useState<string>('form-0') // Key to force form remount

  const { getCurrentAndDescendant } = useCompaniesService()

  const { data: companyData } = useQuery(
    'fetch-companies',
    getCurrentAndDescendant
  )

  const getDefaultCompanyId = () => {
    return currentUser?.company?.id || undefined
  }

  const getBasicUserRoleId = () => {
    const basicUserRole = rolesData?.results?.find(
      (role) => role.name === 'Basic User'
    )
    return basicUserRole?.id || 7
  }

  const getInitialDefaultValues = () => {
    return {
      title: '',
      name: '',
      surname: '',
      email: '',
      company_id: undefined,
      role_id: undefined,
      password: '',
      company_roles: [
        {
          company_id: getDefaultCompanyId()
            ? Number(getDefaultCompanyId())
            : undefined,
          role_id: getBasicUserRoleId()
            ? Number(getBasicUserRoleId())
            : undefined,
        },
      ],
    }
  }

  const [defaultValues, setDefaultValues] = useState(getInitialDefaultValues())

  useEffect(() => {
    if (rolesData && currentUser) {
      setDefaultValues(getInitialDefaultValues())
    }
  }, [rolesData, currentUser])

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<User>({
    defaultValues: defaultValues as any,
    resolver: yupResolver<any>(
      !!user
        ? CreateUpdateUserValidationSchema.omit(['password'])
        : CreateUpdateUserValidationSchema
    ),
  })

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'company_roles',
    keyName: 'fieldId',
  })

  useEffect(() => {
    if (isOpen) {
      setFormKey(`form-${Date.now()}`)

      setTimeout(() => {
        if (user) {
          const companyRoles =
            user.company_roles && user.company_roles.length > 0
              ? user.company_roles
              : defaultValues.company_roles

          const resetData = {
            ...user,
            company_roles: companyRoles,
          }

          replace([])

          reset(resetData)

          setTimeout(() => {
            replace(companyRoles)
          }, 0)
        } else {
          replace([])
          reset(defaultValues)
          setTimeout(() => {
            replace(defaultValues.company_roles)
          }, 0)
        }
      }, 0)
    }
  }, [isOpen, user, reset, replace, defaultValues])

  useEffect(() => {
    if (!isOpen) {
      setUser(undefined)
      setResetPasswordHasBeenSent(false)
    }
  }, [isOpen])

  const createUserMutation = useMutation(createUser, {
    onSuccess: () => {
      void queryClient.invalidateQueries('users-list')
      toggleModal()
      refreshUser()
      successToast('User has been created')
    },
    onError: (error: any) => {
      let errorMessage = 'Cannot create user'

      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error?.detail) {
        errorMessage = error.detail
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      toast.error(errorMessage)
    },
  })

  const updateUserMutation = useMutation(updateUser, {
    onSuccess: (data) => {
      void queryClient.invalidateQueries('users-list')
      if (user?.id) {
        setUser((prev) => ({ ...prev, ...data }))
      }
      toggleModal()
      refreshUser()
      successToast('User has been updated')
    },
    onError: (error: any) => {
      let errorMessage = 'Cannot update user'

      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error?.detail) {
        errorMessage = error.detail
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      toast.error(errorMessage)
    },
  })

  const [resetPasswordHasBeenSent, setResetPasswordHasBeenSent] =
    useState<boolean>(false)

  const resetPasswordRequestMutation = useMutation(forgotPassword, {
    onSuccess: () => {
      successToast(
        "A password reset email has been successfully sent to the user's registered email address"
      )
      setResetPasswordHasBeenSent(true)
    },
    onError: (error: any) => {
      let errorMessage = 'Cannot reset password'

      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error?.detail) {
        errorMessage = error.detail
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      toast.error(errorMessage)
    },
  })

  const companyOptions = companyData?.results?.map(({ name, id }) => ({
    label: name,
    value: id,
  }))

  const rolesOptions = rolesData?.results?.map(({ name, id }) => ({
    label: name,
    value: id,
  }))

  const toggleModal = () => setIsOpen(!isOpen)

  const onSubmit = async (data: User) => {
    try {
      // Remove duplicates from company_roles before submitting
      const uniqueCompanyRoles =
        data.company_roles?.filter(
          (role, index, self) =>
            index ===
            self.findIndex(
              (r) =>
                r.company_id === role.company_id && r.role_id === role.role_id
            )
        ) || []

      const submitData = {
        ...data,
        company_roles: uniqueCompanyRoles,
      }

      if (!!user) {
        await updateUserMutation.mutateAsync({
          id: user?.id,
          ...submitData,
        })
      } else {
        await createUserMutation.mutateAsync({ id: user?.id, ...submitData })
      }
    } catch (_error) {}
  }

  const handleResetPassword = () => {
    if (user?.email) {
      resetPasswordRequestMutation.mutate(user.email)
    } else {
      toast.error('User email is not available')
    }
  }

  const [ResetPasswordModal, openResetPasswordModal] = useDeleteModal({
    onConfirm: handleResetPassword,
    children: "Are you sure you want to reset this user's password?",
    confirmationButtonText: 'Reset',
  })

  const openModal = (userData?: User) => {
    setUser(userData)
    setIsOpen(true)
  }

  const handleAddRole = () => {
    const defaultCompanyId = getDefaultCompanyId()
    const defaultRoleId = getBasicUserRoleId()
    if (defaultCompanyId && defaultRoleId) {
      const currentFields = getValues('company_roles') || []
      const newRole = {
        company_id: Number(defaultCompanyId),
        role_id: Number(defaultRoleId),
      }

      replace([...currentFields, newRole])
    }
  }

  const isReadOnly = !!user
    ? !checkPermission(Entity.USERS, CrudAction.UPDATE)
    : !checkPermission(Entity.USERS, CrudAction.CREATE)

  return [
    <FormEditableContext.Provider
      value={{
        isEditable: !isReadOnly,
      }}
    >
      <Modal
        isOpen={isOpen}
        onClose={toggleModal}
        okButtonType="submit"
        confirmButtonDisabled={isReadOnly}
        title={!!user ? 'Edit User' : 'Create User'}
        onConfirmButtonText="Save"
      >
        <Form key={formKey} onSubmit={handleSubmit(onSubmit)} id="hook-form">
          <Box px="24px" py="20px" sx={sxStyles.mainBoxStyle}>
            <Box sx={sxStyles.modalColumnHeadingBox}>
              <ModalColumnHeading>Details</ModalColumnHeading>
              <Box sx={sxStyles.controllerBox}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      size="small"
                      label="Title"
                      error={!!errors['title']?.message}
                      errorMsg={errors['title']?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      name="name"
                      size="small"
                      label="First Name"
                      error={!!errors['name']?.message}
                      errorMsg={errors['name']?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name={'surname'}
                  control={control}
                  render={({ field }) => (
                    <Input
                      size="small"
                      label="Last Name"
                      error={!!errors['surname']}
                      errorMsg={errors['surname']?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      size="small"
                      label="Email"
                      error={!!errors['email']}
                      errorMsg={errors['email']?.message}
                      {...field}
                    />
                  )}
                />
                {!user ? (
                  <Controller
                    name={'password'}
                    control={control}
                    render={({ field }) => (
                      <Input
                        name="password-new"
                        autoComplete="one-time-code"
                        type="password"
                        size="small"
                        label="Password"
                        error={!!errors['password']}
                        errorMsg={errors['password']?.message}
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <Button
                    color="primary"
                    startIcon={<RestartAltIcon />}
                    variant="text"
                    disabled={isReadOnly || resetPasswordHasBeenSent}
                    onClick={openResetPasswordModal}
                  >
                    Reset Password
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={sxStyles.modalColumnHeadingBox}>
              <ModalColumnHeading>
                Access
                <IconContainer onClick={handleAddRole} disabled={isReadOnly}>
                  <PlusIcon />
                </IconContainer>
              </ModalColumnHeading>
              <RolesWrapper>
                {fields?.length ? (
                  fields?.map((item, index) => {
                    return (
                      <RolesContainer key={item.fieldId}>
                        <RolesInputs>
                          <Controller
                            name={`company_roles.${index}.company_id`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                size="small"
                                options={companyOptions}
                                label="Select Site"
                                placeholder="Select Site"
                                error={
                                  !!errors.company_roles?.[index]?.company_id
                                }
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`company_roles.${index}.role_id`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                size="small"
                                options={rolesOptions}
                                label="Select Role"
                                placeholder="Select Role"
                                error={!!errors.company_roles?.[index]?.role_id}
                                {...field}
                              />
                            )}
                          />
                        </RolesInputs>
                        <DeleteIconContainer>
                          <IconButton
                            disabled={index === 0 || isReadOnly}
                            onClick={() => remove(index)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </DeleteIconContainer>
                      </RolesContainer>
                    )
                  })
                ) : (
                  <Box sx={sxStyles.errorMessageStyle}>
                    User must have at least 1 Role
                  </Box>
                )}
              </RolesWrapper>
            </Box>
          </Box>
        </Form>
        {ResetPasswordModal}
      </Modal>
    </FormEditableContext.Provider>,
    openModal,
  ]
}

export default useUserInfoModal
